import { Chip, HStack, Text } from '@ayble-health/dashboard-components';
import { PmStatus } from 'api/types';
import { formatDateInBrowserTZ } from 'components/UserDashboard/utils';
import { CircleCheckIcon, LockIcon } from 'lucide-react';
import { useToken } from '@chakra-ui/react';
import { apptDidNotOccur } from 'components/ProviderHome/utils';

type Props = {
  clientConfirmed: boolean;
  pmStatus: PmStatus | null;
  confirmMeeting?: () => void;
  handleJoin?: () => void;
  joinable?: boolean;
  openChartNote?: () => void;
  chartNoteLocked?: boolean | null;
  chartNoteSigned?: boolean | null;
  rescheduledFor?: string | null;
};

export const AppointmentBlockBadge = ({
  clientConfirmed,
  pmStatus,
  confirmMeeting,
  joinable,
  handleJoin,
  openChartNote,
  chartNoteLocked,
  chartNoteSigned,
  rescheduledFor,
}: Props) => {
  const [subtle] = useToken('colors', ['subtle']);
  const apptNotOccurred = apptDidNotOccur(pmStatus, !!rescheduledFor);

  if (apptNotOccurred) {
    const label = rescheduledFor ? 'Rescheduled' : pmStatus;

    return (
      <Chip variant={'alert'} px={3} h={'26px'} fontSize={'xs'}>
        {label}
        {!!rescheduledFor && (
          <Text as={'span'} color={'bluegrey'} fontWeight={400} fontSize={'xs'}>
            {' · '}
            {formatDateInBrowserTZ(rescheduledFor, 'MMM D, h:mm A z')}
          </Text>
        )}
      </Chip>
    );
  }

  if (joinable) {
    return (
      <Chip
        size={'md'}
        px={3}
        variant={'solid'}
        h={'26px'}
        onClick={handleJoin}
      >
        Up next
      </Chip>
    );
  }

  if (pmStatus === PmStatus.occurred) {
    if (chartNoteLocked) {
      return (
        <HStack gap={1} alignItems={'center'}>
          <LockIcon color={subtle} size={16} />
          <Text fontSize={'xs'} color="subtle">
            Note locked{' · '}Occurred
          </Text>
        </HStack>
      );
    }

    if (chartNoteSigned) {
      return (
        <HStack gap={1}>
          <CircleCheckIcon color={subtle} size={16} />
          <Text fontSize={'xs'} color="subtle">
            Note signed{' · '}Occurred
          </Text>
        </HStack>
      );
    }

    return (
      <Chip
        size={'md'}
        px={3}
        variant={'outline'}
        h={'26px'}
        onClick={
          !chartNoteLocked && !chartNoteLocked ? openChartNote : undefined
        }
      >
        Edit note
      </Chip>
    );
  }

  if (clientConfirmed) {
    return (
      <Chip size={'md'} px={3} variant={'aqua'} h={'26px'}>
        Confirmed
      </Chip>
    );
  } else {
    return (
      <Chip
        variant={'symptom'}
        size={'md'}
        px={3}
        h={'26px'}
        onClick={confirmMeeting}
      >
        Unconfirmed
      </Chip>
    );
  }
};

export default AppointmentBlockBadge;
